import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import intersection from "lodash/intersection";
import find from "lodash/find";
import map from "lodash/map";
import * as GameApi from "api/GameApi";
import MultiSelectDropdown from "components/common/Dropdown/MultiSelectDropdown";
import { handleErrorResponse } from "utils/request";
import { PermissionTypes, userHasPermission } from "utils/permissions/rolesPermissions";
import { GROWTH, GROWTH_DOSE_POINTS, GROWTH_NGP } from "constants/Games/GameConstants";
import * as UserPermissions from "constants/UserPermissions";

function GameTypeProductMultiSelect({
    canViewProviderBIGames,
    canViewProviderBIGamesDemo,
    categoryId,
    clinicProducts = null,
    comparisonPeriod,
    disabled = false,
    gameType,
    hasRequiredData = false,
    label = "Product(s) Used for Game",
    name,
    onChange,
    placeholder = "Select Products",
    userId = null,
    value = undefined,
}) {
    const [gameTypeProducts, setGameTypeProducts] = useState([]);
    const brandOptions = map(gameTypeProducts, product => {
        return {
            name: product.brandName,
            value: product.productBrandId,
        };
    }) || [];
    const options = brandOptions || [];

    const loadProducts = async (gameType=null, period=null, categoryId=null, userId=null) => {
        try {
            const res = await GameApi.getProductsForGameTypeAndPeriod(gameType, period, categoryId, userId);
            setGameTypeProducts(res.body || []);
        } catch (e) {
            handleErrorResponse("loading game type products", e);
        }
    };

    useEffect(() => {
        if (gameType && categoryId && (canViewProviderBIGames || canViewProviderBIGamesDemo)) {
            loadProducts(gameType, comparisonPeriod, categoryId, userId);
        }
    }, [gameType, comparisonPeriod, categoryId, userId]);

    if (!(canViewProviderBIGames || canViewProviderBIGamesDemo)) {
        return null;
    }

    const handleChange = ({ name, value }) => {
        //    FOR PRODUCT UPDATE
        const newProducts = value.length ? map(value, v => {
            const product = find(gameTypeProducts, { productBrandId: v});
            return {
                productBrandId: v,
                brandName: product?.brandName,
            };
        }) : [];
        onChange({ name, value: newProducts });
    }

    return (
        <>
            {(
                (gameType === GROWTH || gameType === GROWTH_NGP || gameType === GROWTH_DOSE_POINTS) &&
                (
                    !!value?.length &&
                    hasRequiredData &&
                    !intersection(clinicProducts, value)?.length
                )
            ) && (
                <div style={{
                    border: "1px solid orange",
                    borderRadius: "4px",
                    padding: "1em",
                }}>
                    <i className="fa fa-warning text-warn" /> Clinic did not sell the selected product{(value.length > 1) && "s"} during the timeframe.
                </div>
            )}
            <MultiSelectDropdown
                label={label}
                name={name}
                value={value}
                placeholder={placeholder}
                options={options}
                onChange={handleChange}
                disabled={disabled}
            />
        </>
    );
}

GameTypeProductMultiSelect.propTypes = {
    categoryId: PropTypes.number.isRequired,
    clinicProducts: PropTypes.arrayOf(PropTypes.number),
    comparisonPeriod: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    gameType: PropTypes.string.isRequired,
    hasRequiredData: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    userId: PropTypes.number,
    value: PropTypes.array,
};

export default connect(
    (state) => {
        const userProfile = state.user.userProfile;
        const canViewProviderBIGames = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_BI_GAMES, userProfile);
        const canViewProviderBIGamesDemo = userHasPermission(PermissionTypes.VIEW, UserPermissions.PROVIDER_BI_GAMES_DEMO, userProfile);
        return {
            canViewProviderBIGames,
            canViewProviderBIGamesDemo,
        }
    },
)(GameTypeProductMultiSelect);
